// src/components/privacy/PrivacyIntro.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaShieldAlt, FaCheckCircle, FaClock, FaGlobe } from 'react-icons/fa';

const PrivacyIntro = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Key points about privacy policy
  const keyPoints = [
    {
      icon: <FaShieldAlt />,
      title: "Your Privacy Matters",
      description: "We are committed to protecting your personal information and being transparent about how we use it."
    },
    {
      icon: <FaCheckCircle />,
      title: "GDPR & CCPA Compliant",
      description: "Our practices align with international data protection regulations to ensure your rights are respected."
    },
    {
      icon: <FaClock />,
      title: "Regularly Updated",
      description: "This policy is reviewed and updated to reflect our current data handling practices."
    },
    {
      icon: <FaGlobe />,
      title: "Applies to All Services",
      description: "Covers all StayIN websites, mobile apps, and services across all locations."
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    },
  };

  return (
    <section className="py-12 relative overflow-hidden">
  
      <div className="container-custom relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className=""
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center max-w-3xl mx-auto 2xl:mb-8 space-y-2">
            <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
            <span className="text-[#c9a86c] font-medium tracking-[0.2em] text-xs uppercase">
              Introduction
            </span>
            <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
          </div>
            
            <h2 className="text-4xl !mb-4 lg:text-5xl font-light text-[#1a2b3c] leading-tight">
              Our Commitment to Your{' '}
              <span className="font-bold text-[#c9a86c]">Privacy</span>
            </h2>
          </motion.div>

          {/* Main Content */}
          <motion.div variants={itemVariants} className="prose prose-lg max-w-none">
            <div className="bg-gradient-to-b from-[#f1f1f1] via-[#f1f1f1]/0 to-[#f1f1f1]/0 p-8 rounded-2xl border border-gray-200 2xl:mb-8 mb-4">
              <p className="md:text-[17px] 2xl:text-lg  text-gray-600 animate-fade-in-up animation-delay-400 leading-relaxed">
                At <span className="font-semibold text-[#1a2b3c]">StayIN Hospitality</span>, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or stay at our apartments. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Effective Date Card */}
              <motion.div 
                variants={itemVariants}
                className="group p-8 rounded-2xl bg-white shadow-lg hover:shadow-xl transition duration-300 border border-gray-100 hover:border-gray-100"
              >
                <h3 className="text-lg font-semibold text-[#1a2b3c] mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#c9a86c] rounded-full"></span>
                  Effective Date
                </h3>
                <p className="text-gray-600">
                  <span className="text-md font-bold">March 15, 2026</span>
                  <br />
                  <span className="text-lg">Last updated: March 15, 2026</span>
                </p>
              </motion.div>

              {/* Scope Card */}
              <motion.div 
                variants={itemVariants}
                className="group p-8 rounded-2xl bg-white shadow-lg hover:shadow-xl transition duration-300 border border-gray-100 hover:border-gray-100"
              >
                <h3 className="text-lg font-semibold text-[#1a2b3c] mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#c9a86c] rounded-full"></span>
                  Scope
                </h3>
                <p className="md:text-[17px] 2xl:text-lg text-gray-600">
                  This policy applies to all StayIN websites, mobile applications, booking platforms, and services across all our properties in Islamabad.
                </p>
              </motion.div>
            </div>

            {/* Key Points Grid */}
            <motion.div 
              variants={containerVariants}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 2xl:mt-8 mt-4"
            >
              {keyPoints.map((point, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-4 p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 group"
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-[#c9a86c]/10 rounded-full flex items-center justify-center group-hover:bg-[#c9a86c] transition-colors duration-300">
                    <span className="text-[#c9a86c] text-xl group-hover:text-white transition-colors duration-300">
                      {point.icon}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-md font-bold text-[#1a2b3c] mb-1">{point.title}</h4>
                    <p className="md:text-[17px] 2xl:text-lg text-gray-600">{point.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Consent Statement */}
            <motion.div 
              variants={itemVariants}
              className="2xl:mt-8 mt-4 p-8 bg-gradient-to-b from-[#f1f1f1] via-[#f1f1f1]/0 to-[#f1f1f1]/0 rounded-xl border border-[#c9a86c]/20 text-center"
            >
              <p className="text-gray-700">
                <span className="font-bold">By using our services, you consent to our Privacy Policy</span>
                <br />
                <span className="text-lg text-gray-600">If you have any questions, please contact our Data Protection Officer.</span>
              </p>
            </motion.div>

            {/* Quick Navigation Links
            <motion.div 
              variants={itemVariants}
              className="flex flex-wrap justify-center gap-4 mt-8 pt-6 border-t border-gray-200"
            >
              <a href="#collection" className="text-[17px] 2xl:text-lg text-gray-600 hover:text-[#c9a86c] transition">Data Collection</a>
              <span className="text-gray-300">•</span>
              <a href="#usage" className="text-[17px] 2xl:text-lg text-gray-600 hover:text-[#c9a86c] transition">How We Use</a>
              <span className="text-gray-300">•</span>
              <a href="#sharing" className="text-[17px] 2xl:text-lg text-gray-600 hover:text-[#c9a86c] transition">Information Sharing</a>
              <span className="text-gray-300">•</span>
              <a href="#rights" className="text-[17px] 2xl:text-lg text-gray-600 hover:text-[#c9a86c] transition">Your Rights</a>
            </motion.div> */}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default PrivacyIntro;